import { useIntl } from 'react-intl';

import { useGetObjectQuery } from 'App/redux/objectApi';
import { useGetMetadataListQuery } from 'App/redux/MetadataApi';

import { Permissions, AuditLog, IntlErrorBoundary } from '_common/components';
import { Tabs } from '@ets/design-system';
import Metadata from '_common/components/Metadata/Metadata';

import SharedSpaceSettings from './SharedSpaceSettings/SharedSpaceSettings';
import PersonalSpaceSettings from './PersonalSpaceSettings/PersonalSpaceSettings';
import GeneralSettings from './GeneralSettings/GeneralSettings';
import PublicLinkView from './PublicLinkView/PublicLinkView';
import { useGetCurrentUserQuery } from '_common/services/api/authority';
import { ModalData } from 'App/ModalContext/ModalContextTypes';
import { useDispatch, useRequiredParams, useSelector } from '_common/hooks';
import { setNodeToFocus } from 'Editor/redux/EditorStatusSlice';
import { setAnnotationToFocus } from 'PDF/redux/PDFGeneralSlice';
import { setElementToFocus } from 'Presentation/PresentationSlice';
import { navigateToEditor, navigateToPDF, navigateToPresentation } from 'router/navigation';
import { setSelectedTab } from '_common/redux/TabMenuSlice';
import styles from './ObjectSettingsPage.module.scss';

const Body = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { id: objectId, type: objectType } = useRequiredParams<{
    id: ObjectId;
    type: ObjectType;
  }>();

  const { data: object } = useGetObjectQuery(
    { objectId, objectType },
    { skip: !objectId || !objectType },
  );
  const { data: currentUser } = useGetCurrentUserQuery();
  const { data: metadata } = useGetMetadataListQuery(undefined, {
    selectFromResult: (result) => ({ ...result, data: result.data ?? {} }),
  });
  const tab = useSelector((state) => state.tabMenu);

  const navigateToObject: ModalData['DocumentAuditLogModal']['navigateToObject'] = ({
    objectType,
    objectId,
    documentId,
  }) => {
    if (!documentId) {
      return;
    }
    if (object?.type === 'document') {
      dispatch(setNodeToFocus({ objectType, objectId, documentId }));
      navigateToEditor(documentId);
    } else if (object?.type === 'dopdf') {
      if (objectType === 'task') {
        dispatch(setAnnotationToFocus({ objectId, documentId }));
      }
      navigateToPDF(documentId);
    } else if (object?.type === 'presentation') {
      dispatch(setElementToFocus({ objectType, objectId, documentId }));
      navigateToPresentation(documentId);
    }
  };

  switch (object?.type) {
    case 'space':
      if (object.personal) {
        return (
          <Tabs
            value={tab.objectSettingsPage_space_personal || 'general'}
            onValueChange={(value) =>
              dispatch(
                setSelectedTab({ menuId: 'objectSettingsPage_space_personal', selectedTab: value }),
              )
            }
          >
            <Tabs.List>
              <Tabs.Trigger value="general">
                {intl.formatMessage({ id: 'settings.tabs.general' })}
              </Tabs.Trigger>
            </Tabs.List>
            <Tabs.Content value="general">
              <IntlErrorBoundary key="general" size="xl" style={{ margin: '5rem 0' }}>
                <PersonalSpaceSettings />
              </IntlErrorBoundary>
              <div />
            </Tabs.Content>
          </Tabs>
        );
      }
      return (
        <Tabs
          value={tab.objectSettingsPage_space || 'general'}
          onValueChange={(value) =>
            dispatch(setSelectedTab({ menuId: 'objectSettingsPage_space', selectedTab: value }))
          }
        >
          <Tabs.List>
            <Tabs.Trigger value="general">
              {intl.formatMessage({ id: 'settings.tabs.general' })}
            </Tabs.Trigger>
            <Tabs.Trigger value="permissions">
              {intl.formatMessage({ id: 'COLLABORATORS' })}
            </Tabs.Trigger>
          </Tabs.List>
          <Tabs.Content value="general">
            <IntlErrorBoundary key="general" size="xl" style={{ margin: '5rem 0' }}>
              <SharedSpaceSettings />
              <div />
            </IntlErrorBoundary>
          </Tabs.Content>
          <Tabs.Content value="permissions">
            <IntlErrorBoundary key="permissions" size="xl" style={{ margin: '5rem 0' }}>
              <Permissions />
            </IntlErrorBoundary>
          </Tabs.Content>
        </Tabs>
      );
    case 'presentation':
    case 'dopdf':
      return (
        <Tabs
          value={tab.objectSettingsPage_folder || 'general'}
          onValueChange={(value) =>
            dispatch(setSelectedTab({ menuId: 'objectSettingsPage_folder', selectedTab: value }))
          }
        >
          <Tabs.List>
            <Tabs.Trigger value="general">
              {intl.formatMessage({ id: 'settings.tabs.general' })}
            </Tabs.Trigger>
            <Tabs.Trigger value="permissions">
              {intl.formatMessage({ id: 'COLLABORATORS' })}
            </Tabs.Trigger>
            {object.user_permissions.includes('owner') && (
              <Tabs.Trigger value="auditlog">
                {intl.formatMessage({ id: 'AUDIT_LOG' })}
              </Tabs.Trigger>
            )}
          </Tabs.List>
          <Tabs.Content value="general">
            <IntlErrorBoundary key="general" size="xl" style={{ margin: '5rem 0' }}>
              <GeneralSettings />
            </IntlErrorBoundary>
          </Tabs.Content>
          <Tabs.Content value="permissions">
            <IntlErrorBoundary key="permissions" size="xl" style={{ margin: '5rem 0' }}>
              <Permissions />
            </IntlErrorBoundary>
          </Tabs.Content>
          {object.user_permissions.includes('owner') && (
            <Tabs.Content value="auditlog" className={styles.contentTable}>
              <IntlErrorBoundary key="auditlog" size="xl" style={{ margin: '5rem 0' }}>
                <AuditLog
                  objectId={object.id}
                  objectType={object.type}
                  margin="2rem 0 0 0"
                  navigateToObject={navigateToObject}
                />
              </IntlErrorBoundary>
            </Tabs.Content>
          )}
        </Tabs>
      );
    case 'folder':
      return (
        <Tabs
          value={tab.objectSettingsPage_folder || 'general'}
          onValueChange={(value) =>
            dispatch(setSelectedTab({ menuId: 'objectSettingsPage_folder', selectedTab: value }))
          }
        >
          <Tabs.List>
            <Tabs.Trigger value="general">
              {intl.formatMessage({ id: 'settings.tabs.general' })}
            </Tabs.Trigger>
            <Tabs.Trigger value="permissions">
              {intl.formatMessage({ id: 'COLLABORATORS' })}
            </Tabs.Trigger>
          </Tabs.List>
          <Tabs.Content value="general">
            <IntlErrorBoundary key="general" size="xl" style={{ margin: '5rem 0' }}>
              <GeneralSettings />
            </IntlErrorBoundary>
          </Tabs.Content>
          <Tabs.Content value="permissions">
            <IntlErrorBoundary key="permissions" size="xl" style={{ margin: '5rem 0' }}>
              <Permissions />
            </IntlErrorBoundary>
          </Tabs.Content>
        </Tabs>
      );
    case 'document':
      return (
        <Tabs
          value={tab.objectSettingsPage_document || 'general'}
          onValueChange={(value) => {
            dispatch(setSelectedTab({ menuId: 'objectSettingsPage_document', selectedTab: value }));
          }}
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          <Tabs.List>
            <Tabs.Trigger value="general">
              {intl.formatMessage({ id: 'settings.tabs.general' })}
            </Tabs.Trigger>
            <Tabs.Trigger value="permissions">
              {intl.formatMessage({ id: 'COLLABORATORS' })}
            </Tabs.Trigger>
            {Object.keys(metadata).length > 0 && (
              <Tabs.Trigger value="metadata">{intl.formatMessage({ id: 'METADATA' })}</Tabs.Trigger>
            )}
            {(object.user_permissions.includes('owner') || currentUser?.is_superuser) && (
              <Tabs.Trigger value="auditlog">
                {intl.formatMessage({ id: 'AUDIT_LOG' })}
              </Tabs.Trigger>
            )}
          </Tabs.List>
          <Tabs.Content value="general">
            <IntlErrorBoundary key="general" size="xl" style={{ margin: '5rem 0' }}>
              <GeneralSettings />
            </IntlErrorBoundary>
          </Tabs.Content>
          <Tabs.Content value="permissions">
            <IntlErrorBoundary key="permissions" size="xl" style={{ margin: '5rem 0' }}>
              <Permissions />
            </IntlErrorBoundary>
          </Tabs.Content>
          {Object.keys(metadata).length > 0 && (
            <Tabs.Content value="metadata" style={{ overflow: 'auto' }}>
              <IntlErrorBoundary key="metadata" size="xl" style={{ margin: '5rem 0' }}>
                <Metadata id={object.id} setting />
              </IntlErrorBoundary>
            </Tabs.Content>
          )}
          {(object.user_permissions.includes('owner') || currentUser?.is_superuser) && (
            <Tabs.Content value="auditlog" className={styles.contentTable}>
              <IntlErrorBoundary key="auditlog" size="xl" style={{ margin: '5rem 0' }}>
                <AuditLog
                  objectId={object.id}
                  objectType={object.type}
                  margin="2rem 0 0 0"
                  navigateToObject={navigateToObject}
                />
              </IntlErrorBoundary>
            </Tabs.Content>
          )}
        </Tabs>
      );
    case 'file':
      return (
        <Tabs
          value={tab.objectSettingsPage_file || 'general'}
          onValueChange={(value) =>
            dispatch(setSelectedTab({ menuId: 'objectSettingsPage_file', selectedTab: value }))
          }
          className={styles.tabsContainer}
        >
          <Tabs.List>
            <Tabs.Trigger value="general">
              {intl.formatMessage({ id: 'settings.tabs.general' })}
            </Tabs.Trigger>
            <Tabs.Trigger value="permissions">
              {intl.formatMessage({ id: 'COLLABORATORS' })}
            </Tabs.Trigger>
            {object.user_permissions.includes('owner') && (
              <Tabs.Trigger value="public-link">
                {intl.formatMessage({ id: 'settings.publicLink.publicLink' })}
              </Tabs.Trigger>
            )}
          </Tabs.List>
          <Tabs.Content value="general">
            <IntlErrorBoundary key="general" size="xl" style={{ margin: '5rem 0' }}>
              <GeneralSettings />
            </IntlErrorBoundary>
          </Tabs.Content>
          <Tabs.Content value="permissions">
            <IntlErrorBoundary key="permissions" size="xl" style={{ margin: '5rem 0' }}>
              <Permissions />
            </IntlErrorBoundary>
          </Tabs.Content>
          {object.user_permissions.includes('owner') && (
            <Tabs.Content value="public-link" className={styles.content}>
              <IntlErrorBoundary key="public-link" size="xl" style={{ margin: '5rem 0' }}>
                <PublicLinkView />
              </IntlErrorBoundary>
            </Tabs.Content>
          )}
        </Tabs>
      );
  }

  return null;
};

export default Body;
